/* eslint-disable max-lines */
import { Box, Grid } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import debounceLodash from 'lodash.debounce';
import PropTypes from 'prop-types';
import LeadGenerationContext from '../../../context/LeadGenerationContext';
import { getAddressLookupData } from '../../../services/formService';
import { CustomInput } from '../CustomInput/CustomInput';
import { CustomSelect } from '../CustomSelect/CustomSelect';
import { CustomAutoComplete } from '../CustomAutoComplete/CustomAutoComplete';
import { AddressInformationStyles as classes } from './AddressInformationStyles';
import {
  handleMailingSuggestion,
  returnCityList,
  returnStateList,
} from './AddressInformationUtils';
import { commonUtility, commonUtilityStringValue } from '../UtilityFunctions';
import {
  ADDRESSLINE1_MAX_ERR,
  ADDRESS_LINE1,
  ADDRESS_LINE2,
  CITY,
  COUNTRY,
  COUNTRY_ES,
  COUNTRY_ES_BUNDLE,
  EN,
  MANDATORY_FIELD_ERR,
  REQUIRED,
  STATE,
  ZIPCODE,
} from '../../../constants';
import { commonTernary, isBundle, isWebComponentFlow } from '../../commonCode';
import { handleApiEnviroment } from '../../../WebComponents/commonComponents/customUtils';
/** MailingAddress is a child component to AddressInformation
 */
const MailingAddress = (props) => {
  const { t } = useTranslation();
  const leadGenerationContext = useContext(LeadGenerationContext);
  const baseContext = isWebComponentFlow();
  const [mailingAddressLookupDataResponse, setMailingAddressLookupDataResponse] = useState({});
  const [mailingAddressLookupData, setMailingAddressLookupData] = useState([]);
  const { handleChange, isBeneficiaryAddress = false } = props;

  const {
    beneficiaryData,
    beneficiaryDataErrors,
    addressInformation,
    setAddressInformation,
    addressInformationErrors,
    setAddressInformationErrors,
    setIsScreenThreeDirty,
    setIsScreenFourDirty,
    setIsScreenFiveDirty,
    setIsFundingScreenDirty,
    setCurrentPage,
    personalInformation,
    callLookupApi,
    setCallLookupApi,
    productType,
    productName,
    setCallLookupApiHome,
    setCallLookupApiEmp,
    setCallLookupApiHomeSelfEmp,
    flowTypeETB,
    language,
    setBeneficiaryDataErrors,
    setBeneficiaryData,
  } = baseContext;

  useEffect(() => {
    setCurrentPage?.('address-info');
  }, []);

  const setAddress = isBeneficiaryAddress ? setBeneficiaryData : setAddressInformation;
  const setAddressError = isBeneficiaryAddress
    ? setBeneficiaryDataErrors
    : setAddressInformationErrors;

  const handleMailingAutoCompleteData = (addressObject) => {
    setIsScreenThreeDirty(true);
    setIsScreenFourDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    handleMailingSuggestion(addressObject, setAddress, setAddressError, isBeneficiaryAddress);
  };

  const handleMailingAddressLineOneChange = (permanentAddress1) => {
    setIsScreenThreeDirty(true);
    setIsScreenFourDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    const input = 'permanentAddress1';
    let error = '';
    if (permanentAddress1 && permanentAddress1.length > 0 && permanentAddress1.length <= 30) {
      if (
        permanentAddress1.match(/^[A-Za-z0-9'#@%&\\/.\-\s,]+$/) &&
        permanentAddress1?.length >= 2
      ) {
        error = '';
      } else {
        error = '* Invalid address';
      }
    } else if (permanentAddress1 && permanentAddress1.length > 30) {
      error = ADDRESSLINE1_MAX_ERR;
    } else {
      error = MANDATORY_FIELD_ERR;
    }
    if (isBeneficiaryAddress) {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        addressInfo: { ...prevState?.addressInfo, beneficiaryAddress1: permanentAddress1 ?? '' },
      }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        addressInfo: { ...prevState?.addressInfo, beneficiaryAddress1: error },
      }));
    } else {
      setAddressInformation((prevState) => ({ ...prevState, [input]: permanentAddress1 ?? '' }));
      setAddressInformationErrors((prevState) => ({ ...prevState, [input]: error }));
    }
  };
  /** Address lookup for address line 1 within mailing address component
   */
  async function fetchMailingAddressLookup(
    permanentAddress1,
    permanentCountry,
    query,
    accessToken
  ) {
    if (permanentAddress1?.length >= 6) {
      const response = await getAddressLookupData(
        permanentCountry,
        query,
        accessToken,
        setCallLookupApi,
        setCallLookupApiHome,
        setCallLookupApiEmp,
        setCallLookupApiHomeSelfEmp,
        baseContext?.initiateUiResponse,
        { isWCFlow: baseContext?.wcFlow, env: handleApiEnviroment(baseContext?.environment) }
      );
      if (response?.data?.Results) {
        const options = [];
        response.data.Results.forEach((result) => {
          const permanentAddress = result.Address;
          const permanentZipCode = permanentAddress.PostalCode.split('-')[0];
          options.push(
            `${permanentAddress.AddressLine1}, ${permanentAddress.City}, ${permanentAddress.State}, ${permanentZipCode}`
          );
        });
        setMailingAddressLookupData(options);
      }
      setMailingAddressLookupDataResponse(response);
    }
  }

  const mailingDebounceFunc = debounceLodash(
    (permanentAddress1, permanentCountry, query, accessToken) => {
      fetchMailingAddressLookup(permanentAddress1, permanentCountry, query, accessToken);
    },
    500
  );

  const fetchMailingAddress = useCallback(mailingDebounceFunc, []);

  useEffect(() => {
    let query = '';
    if (!isBeneficiaryAddress && addressInformation?.permanentAddress1) {
      query += `${addressInformation.permanentAddress1} `;
    }
    if (!isBeneficiaryAddress && addressInformation?.permanentAddress2) {
      query += `${addressInformation.permanentAddress2} `;
    }
    if (!isBeneficiaryAddress && addressInformation?.permanentState) {
      query += `${addressInformation.permanentState} `;
    }
    if (!isBeneficiaryAddress && addressInformation?.permanentCity) {
      query += `${addressInformation.permanentCity} `;
    }
    if (!isBeneficiaryAddress && addressInformation?.permanentZipCode) {
      query += `${addressInformation.permanentZipCode} `;
    }
    if (isBeneficiaryAddress && beneficiaryData?.addressInfo?.beneficiaryAddress1) {
      query += `${beneficiaryData?.addressInfo?.beneficiaryAddress1} `;
    }
    if (isBeneficiaryAddress && beneficiaryData?.addressInfo?.beneficiaryAddress2) {
      query += `${beneficiaryData?.addressInfo?.beneficiaryAddress2} `;
    }
    if (isBeneficiaryAddress && beneficiaryData?.addressInfo?.beneficiaryState) {
      query += `${beneficiaryData?.addressInfo?.beneficiaryState} `;
    }
    if (isBeneficiaryAddress && beneficiaryData?.addressInfo?.beneficiaryCity) {
      query += `${beneficiaryData?.addressInfo?.beneficiaryCity} `;
    }
    if (isBeneficiaryAddress && beneficiaryData?.addressInfo?.beneficiaryZipCode) {
      query += `${beneficiaryData?.addressInfo?.beneficiaryZipCode} `;
    }
    const permanentAddress1 = isBeneficiaryAddress
      ? beneficiaryData?.addressInfo?.beneficiaryAddress1
        ? beneficiaryData?.addressInfo?.beneficiaryAddress1
        : ''
      : addressInformation?.permanentAddress1
      ? addressInformation?.permanentAddress1
      : '';
    const permanentCountry = isBeneficiaryAddress
      ? beneficiaryData?.addressInfo?.beneficiaryCountry
        ? beneficiaryData?.addressInfo?.beneficiaryCountry
        : ''
      : addressInformation?.permanentCountry
      ? addressInformation.permanentCountry
      : '';
    if (
      isBeneficiaryAddress
        ? beneficiaryData?.addressInfo?.beneficiaryAddress1 &&
          beneficiaryData?.addressInfo?.beneficiaryAddress1?.length >= 6
        : addressInformation?.permanentAddress1 &&
          addressInformation.permanentAddress1?.length >= 6 &&
          callLookupApi
    ) {
      fetchMailingAddress(permanentAddress1, permanentCountry, query, baseContext.accessToken);
    }
  }, [
    baseContext.accessToken,
    addressInformation?.permanentAddress1,
    beneficiaryData?.addressInfo?.beneficiaryAddress1,
  ]);

  const permanentAddrsOptions = () =>
    mailingAddressLookupData &&
    (isBeneficiaryAddress
      ? beneficiaryData?.addressInfo?.beneficiaryAddress1?.length >= 6
      : addressInformation?.permanentAddress1?.length >= 6)
      ? mailingAddressLookupData
      : [];

  return (
    <>
      {!addressInformation?.sameMailingAddress ||
      (isBeneficiaryAddress && beneficiaryData?.addressInfo?.sameBeneficiaryAddress === false) ? (
        <Grid
          container
          size={{ xs: 12 }}
          columnSpacing={isBeneficiaryAddress && 2}
          sx={isBeneficiaryAddress && { display: 'flex' }}
        >
          <Grid
            container
            size={isBeneficiaryAddress ? { xs: 12, md: 6 } : { xs: 12, sm: 12 }}
            sx={classes.input_grid_addr}
          >
            <Grid size={{ xs: 12 }}>
              <CustomAutoComplete
                id="permanentAddress1"
                isBundle={isBundle(productName)}
                dataTestIdAutocomplete="autocomplete_permanent"
                dataTestIdForLabel="mailingAddress1"
                required={!isBundle(productName)}
                inputLabelAriaLabel={`${t(ADDRESS_LINE1, {
                  defaultValue: ADDRESS_LINE1,
                })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                label={
                  <span
                    style={{
                      color:
                        isBundle(productName) && addressInformationErrors?.permanentAddress1
                          ? 'red'
                          : 'inherit',
                    }}
                  >
                    {t(ADDRESS_LINE1, { defaultValue: ADDRESS_LINE1 })}
                  </span>
                }
                options={permanentAddrsOptions()}
                value={
                  isBeneficiaryAddress
                    ? beneficiaryData?.addressInfo?.beneficiaryAddress1
                    : commonUtility(addressInformation?.permanentAddress1)
                }
                handleChange={(event, selectedValue) => {
                  if (
                    (!isBeneficiaryAddress &&
                      addressInformation?.permanentAddress1 &&
                      addressInformation.permanentAddress1?.length >= 6) ||
                    (isBeneficiaryAddress &&
                      beneficiaryData?.addressInfo?.beneficiaryAddress1 &&
                      beneficiaryData?.addressInfo?.beneficiaryAddress1?.length >= 6)
                  ) {
                    setCallLookupApi(true);
                    const index = mailingAddressLookupData.indexOf(selectedValue);
                    const selectedObj = mailingAddressLookupDataResponse.data.Results[index];
                    handleMailingAutoCompleteData(selectedObj);
                  }
                }}
                handleInputChange={(event, newInputValue) => {
                  setCallLookupApi(true);
                  handleMailingAddressLineOneChange(newInputValue);
                }}
                filterOptions={(options) => options}
                maxLength={30}
                autoFocus
                placeholder={t(
                  isBundle(productName) || isBeneficiaryAddress
                    ? 'Enter address line 1'
                    : 'Enter Address Details',
                  {
                    defaultValue:
                      isBundle(productName) || isBeneficiaryAddress
                        ? 'Enter address line 1'
                        : 'Enter Address Details',
                  }
                )}
                fullWidth
                error={
                  isBeneficiaryAddress
                    ? beneficiaryDataErrors?.addressInfo?.beneficiaryAddress1
                    : addressInformationErrors?.permanentAddress1
                }
              />
              <Box component="span" role="alert" sx={classes.error_message}>
                {isBeneficiaryAddress
                  ? t(beneficiaryDataErrors?.addressInfo?.beneficiaryAddress1, {
                      defaultValue: beneficiaryDataErrors?.addressInfo?.beneficiaryAddress1,
                    })
                  : t(addressInformationErrors?.permanentAddress1, {
                      defaultValue: addressInformationErrors?.permanentAddress1,
                    })}
              </Box>
            </Grid>
          </Grid>

          <Grid
            container
            size={isBeneficiaryAddress ? { xs: 12, md: 6 } : { xs: 12, sm: 12 }}
            sx={classes.input_grid_addr}
          >
            <Grid size={{ xs: 12 }}>
              <CustomInput
                id="permanentAddress2"
                isBundle={isBundle(productName)}
                dataTestId="permanentAddress2_id"
                dataTestIdForLabel="permanentAddress2"
                required={false}
                inputLabelAriaLabel={`${t(ADDRESS_LINE2, { defaultValue: ADDRESS_LINE2 })}`}
                label={t(ADDRESS_LINE2, { defaultValue: ADDRESS_LINE2 })}
                autoFocus={false}
                handleChange={handleChange}
                placeholder={t(
                  isBundle(productName) || isBeneficiaryAddress
                    ? 'Enter address line 2'
                    : 'Enter Address Details',
                  {
                    defaultValue:
                      isBundle(productName) || isBeneficiaryAddress
                        ? 'Enter address line 2'
                        : 'Enter Address Details',
                  }
                )}
                fullWidth
                inputProps={{ maxLength: 30 }}
                value={
                  isBeneficiaryAddress
                    ? beneficiaryData?.addressInfo?.beneficiaryAddress2
                    : commonUtility(addressInformation?.permanentAddress2)
                }
                error={
                  isBeneficiaryAddress
                    ? beneficiaryDataErrors?.addressInfo?.beneficiaryAddress2
                    : commonUtility(addressInformationErrors?.permanentAddress2)
                }
                optional={isBundle(productName)}
              />
              <Box component="span" role="alert" sx={classes.error_message}>
                {isBeneficiaryAddress
                  ? t(beneficiaryDataErrors?.addressInfo?.beneficiaryAddress2, {
                      defaultValue: beneficiaryDataErrors?.addressInfo?.beneficiaryAddress2,
                    })
                  : t(addressInformationErrors?.permanentAddress2, {
                      defaultValue: addressInformationErrors?.permanentAddress2,
                    })}
              </Box>
            </Grid>
          </Grid>

          <Grid
            container
            sx={classes.input_grid_addr}
            size={isBeneficiaryAddress ? { xs: 12, md: 6 } : { xs: 12, sm: 12 }}
          >
            <Grid size={{ xs: 12 }}>
              <CustomSelect
                id="permanentCountry"
                isBundle={isBundle(productName)}
                dataTestIdForLabel="mailingCountry"
                required={!isBundle(productName)}
                inputLabelAriaLabel={`${t(COUNTRY, {
                  defaultValue: COUNTRY,
                })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                label={
                  <span
                    style={{
                      color:
                        isBundle(productName) && addressInformationErrors?.permanentCountry
                          ? 'red'
                          : 'inherit',
                    }}
                  >
                    {commonTernary(
                      language === EN,
                      t(COUNTRY, { defaultValue: COUNTRY }),
                      isBundle(productName)
                        ? t(COUNTRY_ES_BUNDLE, { defaultValue: 'País' })
                        : t(COUNTRY_ES, { defaultValue: 'País/Región' })
                    )}
                  </span>
                }
                options={['PR', 'USA', 'VI']}
                handleChange={handleChange}
                fullWidth
                value={
                  isBeneficiaryAddress
                    ? beneficiaryData?.addressInfo?.beneficiaryCountry
                    : commonUtility(addressInformation?.permanentCountry)
                }
                selectAriaLabel={`${t('Aria Label Country', {
                  defaultValue: 'Country Required Choose your country dropdown menu button',
                })}`}
                error={
                  isBeneficiaryAddress
                    ? beneficiaryDataErrors?.addressInfo?.beneficiaryCountry
                    : addressInformationErrors?.permanentCountry
                }
              />
              <Box component="span" role="alert" sx={classes.error_message}>
                {isBeneficiaryAddress
                  ? t(beneficiaryDataErrors?.addressInfo?.beneficiaryCountry, {
                      defaultValue: beneficiaryDataErrors?.addressInfo?.beneficiaryCountry,
                    })
                  : t(addressInformationErrors?.permanentCountry, {
                      defaultValue: addressInformationErrors?.permanentCountry,
                    })}
              </Box>
            </Grid>
          </Grid>

          <Grid
            container
            sx={classes.input_grid_addr}
            size={isBeneficiaryAddress ? { xs: 12, md: 6 } : { xs: 12, sm: 12 }}
          >
            <Grid size={{ xs: 12 }}>
              <CustomSelect
                id="permanentState"
                isBundle={isBundle(productName)}
                dataTestIdForLabel="mailingState"
                required={!isBundle(productName)}
                inputLabelAriaLabel={`${t(STATE, { defaultValue: STATE })} ${t(REQUIRED, {
                  defaultValue: REQUIRED,
                })}`}
                label={
                  <span
                    style={{
                      color:
                        isBundle(productName) && addressInformationErrors?.permanentState
                          ? 'red'
                          : 'inherit',
                    }}
                  >
                    {t(STATE, { defaultValue: STATE })}
                  </span>
                }
                options={returnStateList(
                  'permanentState',
                  addressInformation,
                  personalInformation,
                  beneficiaryData,
                  isBeneficiaryAddress
                )}
                handleChange={handleChange}
                isState
                fullWidth
                value={
                  isBeneficiaryAddress
                    ? beneficiaryData?.addressInfo?.beneficiaryState
                    : commonUtility(addressInformation?.permanentState)
                }
                selectAriaLabel={`${t('Aria Label State', {
                  defaultValue: 'State Required Choose your state dropdown menu button',
                })}`}
                error={
                  isBeneficiaryAddress
                    ? beneficiaryDataErrors?.addressInfo?.beneficiaryState
                    : addressInformationErrors?.permanentState
                }
              />
              <Box component="span" role="alert" sx={classes.error_message}>
                {isBeneficiaryAddress
                  ? t(beneficiaryDataErrors?.addressInfo?.beneficiaryState, {
                      defaultValue: beneficiaryDataErrors?.addressInfo?.beneficiaryState,
                    })
                  : t(addressInformationErrors?.permanentState, {
                      defaultValue: addressInformationErrors?.permanentState,
                    })}
              </Box>
            </Grid>
          </Grid>

          <Grid
            container
            sx={classes.input_grid_addr}
            size={isBeneficiaryAddress ? { xs: 12, md: 6 } : { xs: 12, sm: 12 }}
          >
            <Grid size={{ xs: 12 }}>
              <CustomSelect
                id="permanentCity"
                isBundle={isBundle(productName)}
                dataTestIdForLabel="mailingCity"
                required={!isBundle(productName)}
                inputLabelAriaLabel={`${t(CITY, {
                  defaultValue: CITY,
                })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                label={
                  <span
                    style={{
                      color:
                        isBundle(productName) && addressInformationErrors?.permanentCity
                          ? 'red'
                          : 'inherit',
                    }}
                  >
                    {t(CITY, { defaultValue: CITY })}
                  </span>
                }
                options={returnCityList(
                  'permanentCity',
                  addressInformation,
                  personalInformation,
                  beneficiaryData
                )}
                handleChange={handleChange}
                fullWidth
                value={
                  isBeneficiaryAddress
                    ? beneficiaryData?.addressInfo?.beneficiaryCity
                    : commonUtility(addressInformation?.permanentCity)
                }
                selectAriaLabel={`${t('Aria Label City', {
                  defaultValue: 'City Required Choose your city dropdown menu button',
                })}`}
                error={
                  isBeneficiaryAddress
                    ? beneficiaryDataErrors?.addressInfo?.beneficiaryCity
                    : addressInformationErrors?.permanentCity
                }
              />
              <Box component="span" role="alert" sx={classes.error_message}>
                {isBeneficiaryAddress
                  ? t(beneficiaryDataErrors?.addressInfo?.beneficiaryCity, {
                      defaultValue: beneficiaryDataErrors?.addressInfo?.beneficiaryCity,
                    })
                  : t(addressInformationErrors?.permanentCity, {
                      defaultValue: addressInformationErrors?.permanentCity,
                    })}
              </Box>
            </Grid>
          </Grid>

          <Grid
            container
            sx={classes.input_grid_addr}
            size={isBeneficiaryAddress ? { xs: 12, md: 6 } : { xs: 12, sm: 12 }}
          >
            <Grid size={{ xs: 12 }}>
              <CustomInput
                id="permanentZipCode"
                name="permanentZipCode"
                isBundle={isBundle(productName)}
                dataTestIdForLabel="mailingZipCode"
                required={!isBundle(productName)}
                inputLabelAriaLabel={`${t(ZIPCODE, {
                  defaultValue: ZIPCODE,
                })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                label={
                  <span
                    style={{
                      color:
                        isBundle(productName) && addressInformationErrors?.permanentZipCode
                          ? 'red'
                          : 'inherit',
                    }}
                  >
                    {t(ZIPCODE, { defaultValue: ZIPCODE })}
                  </span>
                }
                autoFocus={false}
                handleChange={handleChange}
                placeholder={t('Enter Zip Code', {
                  defaultValue: 'Enter Zip Code',
                })}
                fullWidth
                inputProps={{
                  maxLength: flowTypeETB === 'EtbFlow' && !isBeneficiaryAddress ? 9 : 5,
                  inputMode: 'numeric',
                }}
                value={
                  isBeneficiaryAddress
                    ? beneficiaryData?.addressInfo?.beneficiaryZipCode
                    : commonUtilityStringValue(addressInformation?.permanentZipCode)
                }
                error={
                  isBeneficiaryAddress
                    ? beneficiaryDataErrors?.addressInfo?.beneficiaryZipCode
                    : commonUtilityStringValue(addressInformationErrors?.permanentZipCode)
                }
              />
              <Box component="span" role="alert" sx={classes.error_message}>
                {isBeneficiaryAddress
                  ? t(beneficiaryDataErrors?.addressInfo?.beneficiaryZipCode, {
                      defaultValue: beneficiaryDataErrors?.addressInfo?.beneficiaryZipCode,
                    })
                  : t(addressInformationErrors?.permanentZipCode, {
                      defaultValue: addressInformationErrors?.permanentZipCode,
                    })}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        ''
      )}
      <span />
    </>
  );
};

export default MailingAddress;

MailingAddress.propTypes = {
  handleChange: PropTypes.func,
  isBeneficiaryAddress: PropTypes.bool,
};
